// Charge attribution for the account-scope composer (gdn-3086).
//
// Opened from the patient page, the composer used to mint every new ad-hoc
// charge with no encounter — there was no picker, so the charge stranded at
// account level by default (more than one paid charge in four; 205 of them,
// $20,903.79, as of 2026-08-26). Billers were typing the attribution into
// the free-text reason because the field they wanted did not exist.
//
// Design rules, deliberate and load-bearing:
// - NOTHING is preselected. The cure half (gdn-3023) refuses to infer an
//   encounter, and a recency default here would be the same inference by
//   another name — on the account that prompted this, two charges'
//   descriptions name the same encounter and only the biller knows which
//   is right. Recency orders the list; it never answers the question.
// - "Account-level" is a listed peer, not a fallthrough: some charges
//   genuinely belong to no encounter (a membership, a returned-payment
//   fee), and a forced-encounter picker with no honest exit just teaches
//   billers to attribute wrongly to get past it. Choosing it is a
//   deliberate, recorded act — distinct from the old "was never asked".
//
// Pure presentational: state, tracking, and payload mapping live in the
// composer that mounts it — SettleSheet, and AddChargeSheet since gdn-4177
// (mirrors the SettleCopayDeclarationSection split).

// Direct file import (no feature barrel) — shared eligibility design tokens.
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, Button, Typography } from '@mui/material';

import {
  type CopayVisitOption,
  visitDescriptor,
} from '../hooks/useRecentEncounterOptions';

import { VisitRow } from './SettleSheetCopayDeclaration';

/** Where the composer's new ad-hoc charge lands. Null = not yet answered,
 * which blocks submit — the question being skippable is how the strands
 * happened. */
export type NewChargeAttribution = { kind: 'encounter'; encounterId: string } | { kind: 'account' };

interface SettleChargeAttributionSectionProps {
  /** Recent encounters, newest first — order, never selection. */
  visits: CopayVisitOption[];
  value: NewChargeAttribution | null;
  onChange: (attribution: NewChargeAttribution) => void;
  isVisitsLoading?: boolean;
  /** Offer older encounters than the ones listed (gdn-4177). Omit to keep
   * the list as given. */
  onShowOlder?: () => void;
  hasOlder?: boolean;
  isLoadingOlder?: boolean;
}

export const SettleChargeAttributionSection = ({
  visits,
  value,
  onChange,
  isVisitsLoading = false,
  onShowOlder,
  hasOlder = false,
  isLoadingOlder = false,
}: SettleChargeAttributionSectionProps) => {
  const selectedVisit =
    value?.kind === 'encounter'
      ? (visits.find(visit => visit.id === value.encounterId) ?? null)
      : null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="overline"
        sx={{ color: ELIG_TOKENS.midnight, fontWeight: 700, display: 'block', mb: 0.5 }}
      >
        Which encounter is this charge for?
      </Typography>
      <Box
        sx={{
          border: `1px solid ${ELIG_TOKENS.lineStrong}`,
          borderRadius: 1,
          overflow: 'hidden',
          mb: 1,
        }}
      >
        {visits.map(visit => (
          <VisitRow
            key={visit.id}
            visit={visit}
            selected={value?.kind === 'encounter' && value.encounterId === visit.id}
            onSelect={() => onChange({ kind: 'encounter', encounterId: visit.id })}
            ariaVerb="Attach the new charge to"
          />
        ))}
        {visits.length === 0 && (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: ELIG_TOKENS.inkFaint, px: 2, py: 1.25 }}
          >
            {isVisitsLoading
              ? 'Loading encounters…'
              : 'No recent encounters found for this account.'}
          </Typography>
        )}
        {/* A charge entered weeks late belongs to a visit that has scrolled
            out of the recent few; the list's length must not decide which
            encounter can take it (gdn-4177). */}
        {onShowOlder && hasOlder && (
          <Box
            sx={{
              px: 2,
              py: 0.5,
              '&:not(:first-of-type)': { borderTop: `1px solid ${ELIG_TOKENS.line}` },
            }}
          >
            <Button
              size="small"
              onClick={onShowOlder}
              disabled={isLoadingOlder}
              sx={{ textTransform: 'none', fontWeight: 600, px: 0 }}
            >
              {isLoadingOlder ? 'Loading older encounters…' : 'Show older encounters'}
            </Button>
          </Box>
        )}
        <AccountLevelRow
          selected={value?.kind === 'account'}
          onSelect={() => onChange({ kind: 'account' })}
        />
      </Box>

      {/* The resulting linkage, in plain words — same grammar as the copay
          declaration's confirmation band. An encounter selection whose
          visit has dropped out of the list renders NO band rather than
          falling through to the account-level sentence: the confirmation
          must never describe a payload it doesn't match (the sheet clears
          such a selection and re-asks). */}
      {value && (value.kind === 'account' || selectedVisit) && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            bgcolor: value.kind === 'encounter' ? ELIG_TOKENS.greenTint : ELIG_TOKENS.bg,
            border:
              value.kind === 'encounter'
                ? '1px solid rgba(4,120,85,0.22)'
                : `1px solid ${ELIG_TOKENS.line}`,
            borderRadius: 1,
            px: 1.5,
            py: 1.25,
          }}
        >
          <LinkOutlinedIcon
            sx={{
              fontSize: 16,
              color: value.kind === 'encounter' ? ELIG_TOKENS.green : ELIG_TOKENS.inkSoft,
              mt: '1px',
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: value.kind === 'encounter' ? ELIG_TOKENS.green : ELIG_TOKENS.inkSoft,
              fontWeight: 600,
              lineHeight: 1.45,
            }}
          >
            {value.kind === 'encounter' && selectedVisit
              ? `The new charge will appear on ${visitDescriptor(selectedVisit)}.`
              : 'The new charge stays at the account level, tied to no encounter.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/** The honest exit: a peer option, styled like the encounter rows so it
 * reads as an answer rather than an escape hatch. */
const AccountLevelRow = ({ selected, onSelect }: { selected: boolean; onSelect: () => void }) => (
  <Box
    onClick={onSelect}
    // Focusable + Enter/Space, same as VisitRow: this picker gates
    // submit, so a mouse-only row is a hard keyboard block.
    tabIndex={0}
    onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    }}
    role="button"
    aria-pressed={selected}
    aria-label="Keep the new charge at the account level"
    sx={{
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr)',
      gap: 1.25,
      alignItems: 'center',
      px: 2,
      py: 1.25,
      cursor: 'pointer',
      bgcolor: selected ? ELIG_TOKENS.greenTint : ELIG_TOKENS.card,
      '&:not(:first-of-type)': { borderTop: `1px solid ${ELIG_TOKENS.line}` },
      '&:hover': { bgcolor: selected ? ELIG_TOKENS.greenTint : ELIG_TOKENS.bg },
    }}
  >
    {selected ? (
      <CheckCircleIcon sx={{ fontSize: 18, color: ELIG_TOKENS.green }} />
    ) : (
      <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: ELIG_TOKENS.inkFaint }} />
    )}
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <AccountBalanceOutlinedIcon sx={{ fontSize: 14, color: ELIG_TOKENS.inkSoft }} />
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 600 }}>
          Account-level — not tied to an encounter
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint, display: 'block' }}>
        A membership, returned-payment fee, or other charge that belongs to no encounter
      </Typography>
    </Box>
  </Box>
);
